/*
 * Lemonade Training Oy copy rights all right reserved.
 * Radar Score & Athlete Profile Type Definitions
 */

import { Timestamp } from "firebase/firestore";
import { DunningState } from "./membership";
import type { MemberListStatus, MemberStage } from "./memberOverview";
import type { ResultEventSource } from "./resultEvents";
import { RADAR_AXES, RadarAxis, ScoringType } from "./movementRegistry";
import { computeAxisLevels, EarnedBadge, xpToLevel } from "./rpg";

/**
 * Radar scores for all 5 axes
 * Each value is 0-100, normalized against age/gender/weight benchmarks
 */
export type RadarScores = Record<RadarAxis, number>;

/**
 * Per-axis XP totals
 */
export type AxisXP = Record<RadarAxis, number>;

/**
 * Per-axis level values
 */
export type AxisLevels = Record<RadarAxis, number>;

/**
 * Athlete radar profile document
 * Stored in: Users/{uid}/AthleteProfile/radar
 *
 * Single document containing all computed fitness data for an athlete.
 * Written exclusively by Cloud Functions (Admin SDK), never by clients.
 *
 * @example
 * ```typescript
 * const profile: AthleteRadarProfile = {
 *   scores: { STR: 78, OLY: 54, GYM: 41, END: 67, WC: 72 },
 *   overallScore: 62,
 *   strongestAxis: "STR",
 *   weakestAxis: "GYM",
 *   xp: { total: 3200, STR: 900, OLY: 600, GYM: 400, END: 700, WC: 600 },
 *   level: 14,
 *   axisLevels: { STR: 16, OLY: 12, GYM: 8, END: 14, WC: 13 },
 *   lastActivityDate: "2026-03-10",
 *   lastCalculatedAt: Timestamp.now(),
 *   calculationVersion: 1,
 * };
 * ```
 */
export type AthleteRadarProfile = {
  /** Normalized scores per axis (0-100) */
  scores: RadarScores;
  /** Average of all axis scores */
  overallScore: number;
  /** Axis with the highest score */
  strongestAxis: RadarAxis;
  /** Axis with the lowest score */
  weakestAxis: RadarAxis;

  /** XP data */
  xp: AxisXP & {
    /** Lifetime total XP across all axes */
    total: number;
  };
  /** Current overall level */
  level: number;
  /** Per-axis level values */
  axisLevels: AxisLevels;

  /** ISO date string of last activity */
  lastActivityDate: string;

  /** When scores were last recalculated */
  lastCalculatedAt: Timestamp | Date;
  /** Schema version for future migration support */
  calculationVersion: number;
};

/**
 * Detail data for a single radar axis drill-down
 * Used by both mobile and web axis detail views
 */
export type AxisDetailIndicator = {
  /** Movement registry entry ID */
  movementID: string;
  /** Display name of the movement */
  movementName: string;
  /** Current value (weight in kg, reps, or seconds) */
  currentValue: number;
  /** Target value for next level */
  targetValue: number;
  /** Current level for this movement */
  currentLevel: number;
  /** Level milestones with their target values */
  levelMilestones: Record<number, number>;
  /** Scoring shape (determines display format) */
  scoringType: ScoringType;
  /** Progress percentage through current level (0-1) */
  progressPercent: number;
};

/**
 * Full axis detail response from the API
 */
export type AxisDetail = {
  /** Which axis this detail is for */
  axis: RadarAxis;
  /** Axis display name (e.g., "Strength") */
  axisDisplayName: string;
  /** Current score for this axis (0-100) */
  score: number;
  /** Contextual message (e.g., "Your weakest axis — focus here") */
  contextMessage: string;
  /** Benchmark profile label (e.g., "Male, 30, 82 kg") */
  benchmarkLabel: string;
  /** Key indicators with progress data */
  indicators: AxisDetailIndicator[];
};

/**
 * Radar axis display names mapping
 */
export const RADAR_AXIS_DISPLAY_NAMES: Record<RadarAxis, string> = {
  STR: "Strength",
  OLY: "Olympic Power",
  GYM: "Gymnastics",
  END: "Endurance",
  WC: "Work Capacity",
} as const;

/**
 * Type guard for AthleteRadarProfile
 *
 * @example
 * ```typescript
 * if (isAthleteRadarProfile(data)) {
 *   console.log(data.overallScore); // TypeScript knows data is AthleteRadarProfile
 * }
 * ```
 */
export function isAthleteRadarProfile(
  obj: unknown,
): obj is AthleteRadarProfile {
  if (typeof obj !== "object" || obj === null) return false;
  const profile = obj as AthleteRadarProfile;
  return (
    typeof profile.scores === "object" &&
    typeof profile.overallScore === "number" &&
    typeof profile.level === "number" &&
    typeof profile.calculationVersion === "number"
  );
}

/* -------------- [ API Request/Response Types ] ------------------ */

/**
 * Request payload for logging a movement result
 * Used by logMovementResult Cloud Function
 */
export type LogMovementResultRequest = {
  /** Movement registry entry ID */
  movementID: string;
  /** Input mode determines which fields are required */
  inputMode: "oneRepMax" | "workingSet" | "workoutTime";
  /** Weight in kg (required for weight-based movements); at most MOVEMENT_RESULT_MAX.weight. */
  weight?: number;
  /** Whole number of reps (required for rep-based movements); at most MOVEMENT_RESULT_MAX.reps. */
  reps?: number;
  /** Whole number of sets (for working set mode); at most MOVEMENT_RESULT_MAX.sets. */
  sets?: number;
  /** Time in seconds (for time-based movements); at most MOVEMENT_RESULT_MAX.time. */
  time?: number;
  /**
   * Calories (for `measureType: "calories"` movements — erg work like
   * Row/Bike/Ski for-calories). Positive. Added in v3.0.0 (LT-523); the
   * function infers handling from the movement's `measureType` rather than a
   * new `inputMode` value. At most MOVEMENT_RESULT_MAX.calories.
   */
  calories?: number;
  /**
   * Distance in metres (for `measureType: "distance"` movements — e.g.
   * `Handstand Walk (Distance)`, `Row (Distance)`). Positive. Added in v3.0.0
   * (LT-523). At most MOVEMENT_RESULT_MAX.distance.
   */
  distance?: number;
  /** Whether this is a binary unlock (for gymnastics) */
  unlocked?: boolean;
  /**
   * When the effort was performed: an ISO-8601 date-time with an explicit zone
   * (`Z` or `±hh:mm`), not before 1900 and at most 24 h ahead of the server
   * clock. Validated by `parseResultDate`; absent or `null` means now.
   */
  date?: string;
  /**
   * Athlete-authored note, capped at MOVEMENT_RESULT_NOTES_MAX_LENGTH after
   * trim. Servers that predate notes ignore it: send it only once the server
   * persists it.
   */
  notes?: string;
  /**
   * The calling app, for the server's result event; absent logs "unknown".
   * Servers that predate it ignore it.
   */
  source?: ResultEventSource;
};

/**
 * Response from logging a movement result
 */
export type LogMovementResultResponse = {
  success: boolean;
  /** XP earned from this result */
  xpEarned: number;
  /** Whether this was a new PR */
  isNewPR: boolean;
  /** Number of related movements updated by cascade */
  cascadeUpdatedCount: number;
  /** Badges earned (if any) */
  badgesEarned: EarnedBadge[];
  /** Updated e1RM if applicable */
  estimated1RM?: number;
  /** Document id of the result just written. */
  recordId?: string;
  /** Error message if not successful */
  error?: string;
};

/**
 * Request payload for coach athlete roster
 */
export type GetAthleteRosterRequest = {
  /**
   * Box ID to query athletes from. Required for box coaches; ignored for
   * independent coaches (they query their CoachAthletes relationship instead).
   */
  boxID?: string;
  /** Sort field */
  sortBy?: "name" | "level" | "lastActive" | RadarAxis;
  /** Sort direction */
  sortDirection?: "asc" | "desc";
  /** Page size, 1–1000 (server default 50). Servers older than 6.3.0 reject values above 200. */
  limit?: number;
  /** Cursor for pagination */
  startAfter?: string;
};

/**
 * A dunning phase worth surfacing to the gym: the member's payment failed and
 * they are either still in grace or already paused. "none" is never projected;
 * absence on the roster item means no issue.
 */
export type PaymentIssue = Exclude<DunningState, "none">;

/**
 * Single athlete item in the roster response
 */
export type AthleteRosterItem = {
  /** Athlete user UID */
  uid: string;
  /** Display name */
  displayName: string;
  /** Profile photo URL */
  photoURL: string | null;
  /** Radar scores for mini chart */
  scores: RadarScores;
  /** Overall score */
  overallScore: number;
  /** Current level */
  level: number;
  /** Score trend direction (comparing to 30 days ago) */
  trend: "up" | "down" | "flat";
  /** Last activity ISO date */
  lastActiveDate: string;
  /** Days since last activity */
  daysSinceLastActive: number;
  /**
   * Present only while the member is in a dunning episode, and only for the
   * gym's owners and admins (F032, 2026-09-26: coaches never receive billing).
   * State only: no card details or dates. Absent from servers older than 6.6.0.
   */
  paymentIssue?: PaymentIssue;
  /**
   * F032 Members list, by class time. ISO start of their latest class at this
   * gym, or null when they never had one. Absent when the server could not
   * read member activity (then show "unknown", never "no class").
   */
  lastClassAt?: string | null;
  /** Classes at this gym in the last 30 days. Absent as above. */
  classesLast30Days?: number;
  /** ISO join date at this gym. Box rosters only. */
  joinedAt?: string | null;
  /**
   * Joined within MEMBER_NEW_DAYS, and not one of the members the gym brought
   * over when it started. Box rosters only, for owners and coaches alike.
   */
  isNewMember?: boolean;
  /**
   * The Members Overview stage, by the same rules and gym days as the
   * Overview, so its tiles and the list's filters agree. For coaches it never
   * uses memberships, so it is never "paused_or_leaving". Absent when class
   * data is not ready.
   */
  memberStage?: MemberStage;
  /** Owners and admins only: the name of the member's current membership. */
  membershipName?: string | null;
  /** Owners and admins only: status for the Members list. */
  memberStatus?: MemberListStatus;
};

/**
 * Response for coach athlete roster
 */
export type GetAthleteRosterResponse = {
  athletes: AthleteRosterItem[];
  /** Total count for pagination */
  totalCount: number;
  /** Whether more results exist */
  hasMore: boolean;
  /**
   * True when the server hit its candidate cap, so the roster stays incomplete
   * even after paging to exhaustion. Absent from servers older than 6.3.0.
   */
  truncated?: boolean;
  /**
   * True when the payment-issue lookup failed, so `paymentIssue` is missing
   * from every item for a reason other than "nobody is behind". Absent from
   * servers older than 6.6.0.
   */
  paymentIssuesUnavailable?: boolean;
};

/** Fields written only by the XP engine; absent until the athlete's first XP award. */
type RadarXPField = "xp" | "level" | "axisLevels" | "lastActivityDate";

/**
 * `Users/{uid}/AthleteProfile/radar` as stored. Two writers merge into it: the
 * radar trigger (scores) and the XP engine (the {@link RadarXPField}s), so a
 * record written before any XP award leaves a scores-only document. Read it
 * through {@link toAthleteRadarProfile}.
 */
export type StoredAthleteRadarProfile = Omit<
  AthleteRadarProfile,
  RadarXPField
> &
  Partial<Pick<AthleteRadarProfile, RadarXPField>>;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const isAxisNumbers = (v: unknown): v is Record<RadarAxis, number> =>
  typeof v === "object" &&
  v !== null &&
  RADAR_AXES.every((axis) =>
    isFiniteNumber((v as Record<string, unknown>)[axis]),
  );

/**
 * The stored radar document as an {@link AthleteRadarProfile}, or `null` when
 * its scores or level are malformed. XP fields are read the way the XP engine
 * reads them: a non-number total or axis counts as zero, a missing `level`
 * follows the total, and missing `axisLevels` derive from the axis XP.
 */
export function toAthleteRadarProfile(
  obj: unknown,
): AthleteRadarProfile | null {
  if (typeof obj !== "object" || obj === null) return null;
  const doc = obj as Partial<Record<keyof AthleteRadarProfile, unknown>>;
  if (
    !isAxisNumbers(doc.scores) ||
    !isFiniteNumber(doc.overallScore) ||
    !isFiniteNumber(doc.calculationVersion)
  ) {
    return null;
  }
  const xp = readXP(doc.xp);
  const level = doc.level ?? xpToLevel(xp.total);
  if (!isFiniteNumber(level)) return null;
  const axisLevels = isAxisNumbers(doc.axisLevels)
    ? doc.axisLevels
    : (computeAxisLevels(pickAxes(xp)) as AxisLevels);
  return {
    ...(obj as StoredAthleteRadarProfile),
    xp,
    level,
    axisLevels,
    lastActivityDate: (doc.lastActivityDate as string | undefined) ?? "",
  };
}

function readXP(v: unknown): AthleteRadarProfile["xp"] {
  const stored = (typeof v === "object" && v !== null ? v : {}) as Record<
    string,
    unknown
  >;
  const num = (x: unknown) => (isFiniteNumber(x) ? x : 0);
  return {
    total: num(stored.total),
    STR: num(stored.STR),
    OLY: num(stored.OLY),
    GYM: num(stored.GYM),
    END: num(stored.END),
    WC: num(stored.WC),
  };
}

function pickAxes(v: Record<RadarAxis, number>): Record<RadarAxis, number> {
  return { STR: v.STR, OLY: v.OLY, GYM: v.GYM, END: v.END, WC: v.WC };
}
